/**
 * AuditPanel — Inline audit review component for the Curator page
 * 
 * Bulk-select workflow: all items start checked, uncheck the ones you
 * don't want, then hit "Approve All Checked".
 * 
 * Notes: click a row to expand an inline note input. Notes auto-save
 * to the backend after 800ms of inactivity.
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    Archive, Plus, Eye, Wrench, ChevronDown, ChevronRight,
    Check, X, Loader2, ArrowRight, MessageSquare,
    AlertTriangle, CheckSquare, Square, Trash2, RefreshCw
} from 'lucide-react';

const API_BASE = import.meta.env.VITE_API_URL
    ? `${import.meta.env.VITE_API_URL}/api`
    : '/api';

const TABLE_META = [
    { value: 'all', label: 'All Tables', icon: '📋' },
    { value: 'kinks', label: 'Kinks', icon: '🔥' },
    { value: 'sex_actions', label: 'Actions', icon: '⚡' },
    { value: 'activities', label: 'Activities', icon: '🎯' },
    { value: 'archetypes', label: 'Archetypes', icon: '🎭' },
    { value: 'body_parts', label: 'Body Parts', icon: '🫦' },
    { value: 'dynamics', label: 'Dynamics', icon: '⚖️' },
    { value: 'gear', label: 'Gear', icon: '🔧' },
    { value: 'characters', label: 'Characters', icon: '👤' },
    { value: 'scenarios', label: 'Scenarios', icon: '🎬' },
    { value: 'positions', label: 'Positions', icon: '🤸' },
    { value: 'arousal_mechanics', label: 'Mechanics', icon: '🧠' },
    { value: 'arousal_effects', label: 'Effects', icon: '✨' },
];

const PRIORITY_COLORS = {
    P0: 'bg-red-500/20 text-red-400 border-red-500/30',
    P1: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    P2: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    P3: 'bg-green-500/20 text-green-400 border-green-500/30',
};

const ACTION_LABELS = { archive: 'ARCHIVE', review: 'REVIEW', add: 'ADD', fix: 'FIX' };
const ACTION_LABEL_COLORS = {
    archive: 'bg-red-500/20 text-red-400',
    review: 'bg-amber-500/20 text-amber-400',
    add: 'bg-emerald-500/20 text-emerald-400',
    fix: 'bg-blue-500/20 text-blue-400',
};

// ─── Suggestion Row ─────────────────────────────────────────────────────────
function SuggestionRow({ suggestion, isChecked, isActive, onToggle, onClick, notes, onNoteChange }) {
    const prioClass = PRIORITY_COLORS[suggestion.priority] || PRIORITY_COLORS.P3;
    const hasNote = !!(suggestion.note || notes[suggestion.id]);
    const noteInputRef = useRef(null);
    const labelColor = ACTION_LABEL_COLORS[suggestion.action] || ACTION_LABEL_COLORS.review;
    const displayText = suggestion.summary || suggestion.reason || '(no details)';
    const hasReason = suggestion.reason && suggestion.reason !== suggestion.summary;

    return (
        <div className={`rounded-lg transition-all ${isActive ? 'bg-white/[0.04] ring-1 ring-amber-500/20' : ''}`}>
            <div
                onClick={onClick}
                className={`
                    flex items-start gap-2.5 px-3 py-2.5 cursor-pointer transition-all
                    ${!isActive ? 'hover:bg-white/[0.03]' : ''}
                    ${!isChecked ? 'opacity-30' : ''}
                `}
            >
                <button
                    onClick={(e) => { e.stopPropagation(); onToggle(); }}
                    className="flex-shrink-0 mt-0.5"
                >
                    {isChecked
                        ? <CheckSquare className="w-4 h-4 text-emerald-500" />
                        : <Square className="w-4 h-4 text-slate-600" />}
                </button>

                <div className="flex-1 min-w-0">
                    {/* Line 1: action badge + what to do */}
                    <div className="flex items-center gap-2">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold tracking-wider flex-shrink-0 ${labelColor}`}>
                            {ACTION_LABELS[suggestion.action] || 'REVIEW'}
                        </span>
                        <span className="text-[13px] text-slate-200 leading-snug">
                            {displayText}
                        </span>
                    </div>

                    {/* Line 2: WHY — always visible */}
                    {hasReason && (
                        <p className="text-[12px] text-slate-400 mt-1 leading-relaxed border-l-2 border-slate-600/40 pl-2 ml-1">
                            {suggestion.reason}
                        </p>
                    )}

                    {/* Note input — only on expand */}
                    {isActive && (
                        <input
                            ref={noteInputRef}
                            type="text"
                            placeholder="Add a note…"
                            value={notes[suggestion.id] ?? suggestion.note ?? ''}
                            onChange={(e) => onNoteChange(suggestion.id, e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            className="w-full mt-2 px-2.5 py-1.5 text-xs rounded-md bg-slate-800/80 border border-white/10 text-slate-300 placeholder-slate-600 focus:border-amber-500/50 focus:outline-none transition-colors"
                        />
                    )}
                </div>

                <div className="flex items-center gap-1.5 flex-shrink-0 mt-0.5">
                    {hasNote && !isActive && (
                        <MessageSquare className="w-3 h-3 text-amber-500/60" />
                    )}
                    <span className={`text-[10px] px-1.5 py-0.5 rounded border font-mono ${prioClass}`}>
                        {suggestion.priority}
                    </span>
                </div>
            </div>
        </div>
    );
}



// ─── Main Panel ─────────────────────────────────────────────────────────────
export default function AuditPanel({ showToast: externalToast }) {
    const [suggestions, setSuggestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeIndex, setActiveIndex] = useState(0);
    const [tableFilter, setTableFilter] = useState('all');
    const [priorityFilter, setPriorityFilter] = useState('all');
    const [actionFilter, setActionFilter] = useState('all');
    const [checked, setChecked] = useState(new Set());
    const [actionLoading, setActionLoading] = useState(false);
    const [toast, setToast] = useState(null);
    const [stats, setStats] = useState(null);
    const [collapsed, setCollapsed] = useState(new Set());
    const [notes, setNotes] = useState({});  // { id: noteText }
    const listRef = useRef(null);
    const saveTimerRef = useRef(null);

    const showToast = useCallback((msg, type = 'info') => {
        if (externalToast) { externalToast(msg, type); return; }
        setToast({ msg, type });
        setTimeout(() => setToast(null), 4000);
    }, [externalToast]);

    // ─── Note saving (debounced) ─────────────────────────────────────
    const saveNote = useCallback(async (id, note) => {
        try {
            await fetch(`${API_BASE}/audit/note`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id, note }),
            });
        } catch { /* silent — will retry on next edit */ }
    }, []);

    const handleNoteChange = useCallback((id, value) => {
        setNotes(prev => ({ ...prev, [id]: value }));

        // Debounced save
        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        saveTimerRef.current = setTimeout(() => saveNote(id, value), 800);
    }, [saveNote]);

    // Save any pending note on unmount
    useEffect(() => {
        return () => {
            if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        };
    }, []);

    // ─── Fetch ──────────────────────────────────────────────────────
    const fetchSuggestions = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ status: 'pending' });
            if (tableFilter !== 'all') params.set('table', tableFilter);
            if (priorityFilter !== 'all') params.set('priority', priorityFilter);
            if (actionFilter !== 'all') params.set('action', actionFilter);

            const res = await fetch(`${API_BASE}/audit/suggestions?${params}`);
            const data = await res.json();
            const items = data.suggestions || [];
            setSuggestions(items);
            setChecked(new Set(items.map(s => s.id)));
            // Load saved notes into state
            const savedNotes = {};
            items.forEach(s => { if (s.note) savedNotes[s.id] = s.note; });
            setNotes(prev => ({ ...savedNotes, ...prev }));
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [tableFilter, priorityFilter, actionFilter]);

    const fetchStats = useCallback(async () => {
        try {
            const res = await fetch(`${API_BASE}/audit/stats`);
            setStats(await res.json());
        } catch { /* silent */ }
    }, []);

    useEffect(() => { fetchSuggestions(); fetchStats(); }, [fetchSuggestions, fetchStats]);
    useEffect(() => { setActiveIndex(0); }, [tableFilter, priorityFilter, actionFilter]);

    // ─── Check toggling ─────────────────────────────────────────────
    const toggleCheck = useCallback((id) => {
        setChecked(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    }, []);

    const checkAll = useCallback(() => setChecked(new Set(suggestions.map(s => s.id))), [suggestions]);
    const uncheckAll = useCallback(() => setChecked(new Set()), []);

    const toggleTableCheck = useCallback((tableName) => {
        const ids = suggestions.filter(s => s.table === tableName).map(s => s.id);
        setChecked(prev => {
            const next = new Set(prev);
            const allChecked = ids.every(id => next.has(id));
            ids.forEach(id => allChecked ? next.delete(id) : next.add(id));
            return next;
        });
    }, [suggestions]);

    // ─── Bulk actions ───────────────────────────────────────────────
    const bulkAction = useCallback(async (decision) => {
        const ids = [...checked];
        if (ids.length === 0) { showToast('Nothing selected', 'error'); return; }

        setActionLoading(true);
        try {
            const res = await fetch(`${API_BASE}/audit/bulk-review`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ids, decision }),
            });
            const data = await res.json();
            if (!data.success) throw new Error(data.error || 'Failed');

            const icon = decision === 'approve' ? '✓' : '✗';
            showToast(`${icon} ${data.processed} items ${decision}d (${data.failed} failed)`, 'success');
            fetchSuggestions();
            fetchStats();
        } catch (err) {
            showToast(`Error: ${err.message}`, 'error');
        } finally {
            setActionLoading(false);
        }
    }, [checked, showToast, fetchSuggestions, fetchStats]);

    // ─── Keyboard shortcuts ─────────────────────────────────────────
    useEffect(() => {
        const handler = (e) => {
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

            if (e.key === ' ') {
                e.preventDefault();
                const s = suggestions[activeIndex];
                if (s) toggleCheck(s.id);
                return;
            }
            if (e.key === 'ArrowDown' || e.key === 'j') {
                e.preventDefault();
                setActiveIndex(i => Math.min(i + 1, suggestions.length - 1));
            }
            if (e.key === 'ArrowUp' || e.key === 'k') {
                e.preventDefault();
                setActiveIndex(i => Math.max(i - 1, 0));
            }
            if (e.key === 'n') {
                // Focus the note input of the active row
                const noteInput = document.querySelector(`[data-idx="${activeIndex}"] input`);
                if (noteInput) { e.preventDefault(); noteInput.focus(); }
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [suggestions, activeIndex, toggleCheck]);

    useEffect(() => {
        const el = listRef.current?.querySelector(`[data-idx="${activeIndex}"]`);
        el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }, [activeIndex]);

    // ─── Group by table ─────────────────────────────────────────────
    const grouped = useMemo(() => {
        const groups = new Map();
        suggestions.forEach((s, i) => {
            if (!groups.has(s.table)) groups.set(s.table, []);
            groups.get(s.table).push({ ...s, _idx: i });
        });
        return [...groups.entries()];
    }, [suggestions]);

    const checkedCount = checked.size;
    const pendingCount = stats?.byStatus?.pending || 0;
    const approvedCount = stats?.byStatus?.approved || 0;
    const rejectedCount = stats?.byStatus?.rejected || 0;
    const totalCount = stats?.total || 0;

    return (
        <>
            {/* ─── Top toolbar (matches curator style) ─── */}
            <div className="sticky top-16 z-30 bg-[#0a0a0f]/95 backdrop-blur-md border-b border-white/[0.04]">
                <div className="px-5 py-3 flex items-center gap-3">
                    {/* Table filter */}
                    <select
                        value={tableFilter}
                        onChange={e => setTableFilter(e.target.value)}
                        className="px-2.5 py-1.5 text-xs rounded-lg bg-slate-800/80 border border-white/10 text-white focus:border-amber-500 focus:outline-none"
                    >
                        {TABLE_META.map(t => (
                            <option key={t.value} value={t.value}>{t.icon} {t.label}</option>
                        ))}
                    </select>

                    {/* Priority pills */}
                    <div className="flex gap-0.5 bg-slate-800/50 rounded-lg p-0.5">
                        {['all', 'P0', 'P1', 'P2', 'P3'].map(p => (
                            <button key={p} onClick={() => setPriorityFilter(p)}
                                className={`px-2 py-1 text-[11px] font-mono font-medium rounded-md transition-all ${priorityFilter === p
                                    ? p === 'all' ? 'bg-amber-600 text-white' :
                                        p === 'P0' ? 'bg-red-600 text-white' :
                                            p === 'P1' ? 'bg-orange-600 text-white' :
                                                p === 'P2' ? 'bg-yellow-600 text-white' : 'bg-green-600 text-white'
                                    : 'text-slate-400 hover:text-white hover:bg-white/5'}`}>
                                {p === 'all' ? 'All' : p}
                            </button>
                        ))}
                    </div>

                    {/* Action type */}
                    <div className="flex gap-0.5 bg-slate-800/50 rounded-lg p-0.5">
                        {['all', 'archive', 'review', 'add'].map(a => (
                            <button key={a} onClick={() => setActionFilter(a)}
                                className={`px-2 py-1 text-[11px] font-medium rounded-md transition-all capitalize ${actionFilter === a ? 'bg-amber-600 text-white' : 'text-slate-400 hover:text-white hover:bg-white/5'}`}>
                                {a}
                            </button>
                        ))}
                    </div>

                    {/* Stats */}
                    <div className="ml-auto flex items-center gap-3 text-xs text-slate-500 tabular-nums">
                        <span className="text-emerald-400">{approvedCount}✓</span>
                        <span className="text-red-400">{rejectedCount}✗</span>
                        <span>{pendingCount} pending</span>
                        {totalCount > 0 && (
                            <div className="w-32 h-1 bg-slate-800 rounded-full overflow-hidden">
                                <div className="h-full flex">
                                    <div className="bg-emerald-500 transition-all" style={{ width: `${(approvedCount / totalCount) * 100}%` }} />
                                    <div className="bg-red-500 transition-all" style={{ width: `${(rejectedCount / totalCount) * 100}%` }} />
                                </div>
                            </div>
                        )}
                    </div>

                    <button onClick={() => { fetchSuggestions(); fetchStats(); }}
                        className="p-1.5 rounded-md hover:bg-white/5 text-slate-400 hover:text-white transition-colors">
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                    </button>
                </div>

                {/* Bulk action bar */}
                <div className="px-5 pb-3">
                    <div className="flex items-center gap-3 py-2 px-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                        <span className="text-sm font-medium text-amber-400">{checkedCount}/{suggestions.length} checked</span>
                        <span className="text-[10px] text-slate-600">(review only — no DB changes)</span>
                        <div className="h-4 w-px bg-white/10" />
                        <button onClick={checkAll}
                            className="px-2 py-1 text-[11px] text-slate-400 hover:text-white rounded hover:bg-white/5 transition-colors">
                            All
                        </button>
                        <button onClick={uncheckAll}
                            className="px-2 py-1 text-[11px] text-slate-400 hover:text-white rounded hover:bg-white/5 transition-colors">
                            None
                        </button>
                        <div className="h-4 w-px bg-white/10" />
                        <button
                            onClick={() => bulkAction('approve')}
                            disabled={checkedCount === 0 || actionLoading}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-emerald-600/80 text-white hover:bg-emerald-600 transition-colors disabled:opacity-30"
                        >
                            {actionLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                            Mark Approved ({checkedCount})
                        </button>
                        <button
                            onClick={() => bulkAction('reject')}
                            disabled={checkedCount === 0 || actionLoading}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-red-600/80 text-white hover:bg-red-600 transition-colors disabled:opacity-30"
                        >
                            <Trash2 className="w-3 h-3" /> Mark Rejected
                        </button>
                        <span className="text-[10px] text-slate-600 ml-auto hidden lg:inline">
                            space=toggle · ↑↓=navigate · n=note
                        </span>
                    </div>
                </div>
            </div>

            {/* ─── Content ─── */}
            <div className="px-5 py-4" ref={listRef}>
                {error && (
                    <div className="p-4 mb-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                        {error}
                    </div>
                )}

                {loading && suggestions.length === 0 ? (
                    <div className="py-20 text-center text-slate-500">
                        <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                        Loading audit suggestions…
                    </div>
                ) : suggestions.length === 0 ? (
                    <div className="py-20 text-center text-slate-500">
                        <div className="text-4xl mb-2">✓</div>
                        <p>Queue empty — all suggestions reviewed</p>
                        <p className="text-xs mt-1 opacity-60">Change filters to see reviewed items</p>
                    </div>
                ) : (
                    <div className="space-y-0.5">
                        {grouped.map(([table, items]) => {
                            const tableMeta = TABLE_META.find(t => t.value === table);
                            const isCollapsed = collapsed.has(table);
                            const tableItemIds = items.map(s => s.id);
                            const allTableChecked = tableItemIds.every(id => checked.has(id));
                            const tableCheckedCount = items.filter(s => checked.has(s.id)).length;

                            return (
                                <div key={table}>
                                    <div className="flex items-center gap-2 py-1.5 px-1 sticky top-[7.5rem] z-10 bg-[#0a0a0f]/95 backdrop-blur-sm">
                                        <button onClick={() => setCollapsed(prev => {
                                            const n = new Set(prev);
                                            n.has(table) ? n.delete(table) : n.add(table);
                                            return n;
                                        })} className="text-slate-600 hover:text-slate-300">
                                            {isCollapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                                        </button>
                                        <button
                                            onClick={() => toggleTableCheck(table)}
                                            className="text-slate-600 hover:text-white transition-colors"
                                        >
                                            {allTableChecked
                                                ? <CheckSquare className="w-3.5 h-3.5 text-emerald-500" />
                                                : <Square className="w-3.5 h-3.5" />}
                                        </button>
                                        <span className="text-sm">{tableMeta?.icon || '📋'}</span>
                                        <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                                            {tableMeta?.label || table}
                                        </span>
                                        <span className="text-[10px] text-slate-600 font-mono">
                                            ({tableCheckedCount}/{items.length})
                                        </span>
                                        <div className="flex-1 h-px bg-white/[0.04]" />
                                    </div>
                                    {!isCollapsed && items.map(s => (
                                        <div key={s.id} data-idx={s._idx}>
                                            <SuggestionRow
                                                suggestion={s}
                                                isChecked={checked.has(s.id)}
                                                isActive={s._idx === activeIndex}
                                                onToggle={() => toggleCheck(s.id)}
                                                onClick={() => setActiveIndex(s._idx)}
                                                notes={notes}
                                                onNoteChange={handleNoteChange}
                                            />
                                        </div>
                                    ))}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Internal toast (used when no external toast is provided) */}
            {!externalToast && toast && (
                <div className={`fixed top-20 right-4 z-50 px-4 py-2.5 rounded-lg shadow-2xl text-sm font-medium ${toast.type === 'success' ? 'bg-emerald-600 text-white' :
                    toast.type === 'error' ? 'bg-red-600 text-white' :
                        'bg-slate-700 text-slate-200'
                    }`}>
                    {toast.msg}
                </div>
            )}

            {/* Full-screen loading overlay */}
            {actionLoading && (
                <div className="fixed inset-0 bg-black/30 z-40 flex items-center justify-center">
                    <div className="bg-slate-900 rounded-xl border border-white/10 px-8 py-6 flex flex-col items-center gap-3">
                        <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
                        <span className="text-slate-300 text-sm">Processing {checkedCount} suggestions…</span>
                    </div>
                </div>
            )}
        </>
    );
}
